import { Holding } from "@boark/shared/types/holding";
import { Lease } from "@boark/shared/types/lease";
import { Organization } from "@boark/shared/types/organization";
import { Person } from "@boark/shared/types/person";
import { Sublet } from "@boark/shared/types/sublet";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useLocale } from "../stores/locale";
import { formatPercentage } from "../utils/format-percentage";
import { formatPhoneNumber } from "../utils/phone";
import { Dropdown } from "./dropdown";
import { Tooltip } from "./tooltip";

export function OwnerDropdown({
  holding,
  lease,
  sublet,
  showSubletPrefix = true,
}: {
  holding?: Holding;
  lease?: Lease;
  sublet?: Sublet;
  showSubletPrefix?: boolean;
}) {
  return (
    <Dropdown
      buttonSeverity="secondary"
      buttonSize="sm"
      itemClassName="hover:bg-gray-400"
      listClassName="bg-gray-200 whitespace-nowrap rounded-sm border-1 border-black"
      options={[
        ...(holding?.owners.map((owner) => ({
          owner,
          ownerShipType: "owner",
        })) || []),
        ...(lease?.tenants.map((tenant) => ({
          owner: tenant,
          ownerShipType: "tenant",
        })) || []),
        ...(sublet?.tenants.map((subtenant) => ({
          owner: subtenant,
          ownerShipType: "subtenant",
        })) || []),
      ].map(({ owner, ownerShipType }) => ({
        label: (
          <OwnerActionLabel
            owner={owner}
            ownerShipType={ownerShipType as "owner" | "tenant" | "subtenant"}
            showSubletPrefix={showSubletPrefix}
          />
        ),
      }))}
    />
  );
}

function OwnerActionLabel({
  ownerShipType,
  showSubletPrefix,
  owner: { person, organization, primary, share },
}: {
  ownerShipType: "owner" | "tenant" | "subtenant";
  showSubletPrefix: boolean;
  owner: {
    person?: Person;
    organization?: Organization;
    primary?: boolean;
    share?: number;
  };
}) {
  const { get: getLocale } = useLocale();

  return (
    <div className="w-full flex">
      {share && <>{formatPercentage(share)} -</>}
      {ownerShipType === "subtenant" && showSubletPrefix && (
        <span className="font-bold">
          {getLocale("subtenant.subtenant")}:&nbsp;
        </span>
      )}
      {person ? (
        <Person person={person} primary={primary} />
      ) : organization ? (
        <Organization organization={organization} primary={primary} />
      ) : null}
    </div>
  );
}

function Person({ person, primary }: { person: Person; primary?: boolean }) {
  const { get: getLocale } = useLocale();
  return (
    <div className="w-full flex gap-2 justify-between">
      <Tooltip content={person.personalIdentificationNumber}>
        {person.firstName} {person.lastName}
      </Tooltip>
      {primary && (
        <Tooltip content={getLocale("apartment.primaryOwner")}>
          <span className="text-red-500">*</span>
        </Tooltip>
      )}
      {person.email && person.phone && (
        <Contact email={person.email} phone={person.phone} />
      )}
    </div>
  );
}

function Organization({
  organization,
  primary,
}: {
  organization: Organization;
  primary?: boolean;
}) {
  const { get: getLocale } = useLocale();

  return (
    <div className="w-full flex gap-2 justify-between">
      <Tooltip content={organization.corporateRegistrationNumber}>
        {organization.name}
      </Tooltip>
      {primary && (
        <Tooltip content={getLocale("apartment.primaryOwner")}>
          <span className="text-red-500">*</span>
        </Tooltip>
      )}
      {organization.email && organization.phone && (
        <>
          -
          <Contact email={organization.email} phone={organization.phone} />
        </>
      )}
    </div>
  );
}

function Contact({ email, phone }: { email?: string; phone?: string }) {
  return (
    <div className="flex gap-2">
      {email && (
        <Tooltip
          content={
            <a className="text-blue-500" href={`mailto:${email}`}>
              {email}
            </a>
          }
        >
          <FontAwesomeIcon className="text-blue-500" icon={faEnvelope} />
        </Tooltip>
      )}
      {phone && (
        <Tooltip
          content={
            <a className="text-blue-500" href={`tel:${phone}`}>
              {formatPhoneNumber(phone)}
            </a>
          }
        >
          <FontAwesomeIcon
            className="text-blue-500 cursor-pointer"
            icon={faPhone}
          />
        </Tooltip>
      )}
    </div>
  );
}
